'use client';

import { CategoryCard } from '@/cards/CategoryCard';
import { VideoCard } from '@/cards/VideoCard';
import { PlaylistCard } from '@/features/playlist';
import { logger } from '@/utils/loggerUtils';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { useLocale } from '@vimond/i18n';
import {
  CarouselType,
  ListType,
  SmartListType,
} from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import React, {
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { useDebouncedCallback } from 'use-debounce';
import { useCarouselData } from '../../hooks/useCarouselData';
import { useCarouselResponsiveSettings } from '../../hooks/useCarouselResponsiveSettings';
import { CarouselElementType } from '../../types';
import { AssetHero } from '../elements/AssetHero';
import { CategoryHero } from '../elements/CategoryHero';
import './Carousel.css';

type CarouselProps = {
  element: ListType | CarouselType | SmartListType;
  autoplay?: boolean;
  hero?: boolean;
};

const DEFAULT_ITEMS_PER_PAGE = 4;
const PRELOAD_THRESHOLD = 3; // Load more when 3 screens worth of content remaining
const DEBOUNCE_DELAY = 300;

const CustomArrow = memo(
  ({
    direction,
    hero,
    onClick,
  }: {
    direction?: 'left' | 'right';
    hero?: boolean;
    onClick?: () => void;
  }) => (
    <button
      className={cn(
        'group absolute top-1/2 z-50 -translate-y-1/2 transform rounded-full bg-black p-2 opacity-50 hover:bg-white transition-all duration-200',
        direction === 'left'
          ? hero
            ? 'left-7'
            : 'left-3'
          : hero
            ? 'right-7'
            : 'right-3',
      )}
      onClick={onClick}
      aria-label={`${direction === 'left' ? 'Previous' : 'Next'} slide`}
    >
      {direction === 'left' ? (
        <ArrowLeftIcon className="h-6 w-6 text-white group-hover:text-black transition-colors duration-200" />
      ) : (
        <ArrowRightIcon className="h-6 w-6 text-white group-hover:text-black transition-colors duration-200" />
      )}
    </button>
  ),
);
CustomArrow.displayName = 'CustomArrow';

const LoadingIndicator = memo(() => (
  <div className="absolute top-4 right-4 z-50 flex items-center gap-2 bg-black/70 text-white px-3 py-2 rounded-lg backdrop-blur-sm shadow-lg">
    <ArrowPathIcon className="h-4 w-4 animate-spin" />
    <span className="text-sm font-medium">Loading...</span>
  </div>
));
LoadingIndicator.displayName = 'LoadingIndicator';

const ElementRenderer = memo(
  ({
    autoplay,
    element,
    hero,
    numElementsPerPage,
  }: {
    element: CarouselElementType;
    hero: boolean;
    numElementsPerPage: number;
    autoplay?: boolean;
  }) => {
    const { contentType, id: elementId } = element;

    if (contentType === 'asset') {
      if (hero) {
        return (
          <div className={cn(numElementsPerPage > 1 && 'px-1')}>
            <AssetHero
              key={elementId}
              asset={element}
              autoplay={autoplay}
              className={cn(numElementsPerPage > 1 && 'border')}
            />
          </div>
        );
      }
      return <VideoCard asset={element} key={elementId} />;
    }

    if (contentType === 'category') {
      if (hero) {
        return (
          <div className={cn(numElementsPerPage > 1 && 'h-full w-full px-1')}>
            <CategoryHero
              key={elementId}
              category={element}
              className={cn(numElementsPerPage > 1 && 'border')}
            />
          </div>
        );
      }
      return <CategoryCard category={element} key={elementId} />;
    }

    if (contentType === 'contentpanel' || contentType === 'list_smart') {
      return <PlaylistCard playlist={element} key={elementId} />;
    }

    logger.warn(
      'Unsupported element type in carousel:',
      contentType,
      elementId,
    );
    return null;
  },
);
ElementRenderer.displayName = 'ElementRenderer';

const shouldDisplayNextArrow = (
  elements: CarouselElementType[],
  currentSlide: number,
  responsiveSettings: {
    breakpoint: number;
    settings: { slidesToShow: number };
  }[],
  numElementsPerPage: number,
): boolean => {
  const currentSlidesToShow =
    typeof window !== 'undefined'
      ? (responsiveSettings.find(
          (setting) => window.innerWidth <= setting.breakpoint,
        )?.settings.slidesToShow ?? numElementsPerPage)
      : numElementsPerPage;

  return elements.length > currentSlide + currentSlidesToShow;
};

const InfiniteCarousel = ({
  autoplay,
  element,
  hero = false,
}: CarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<Slider>(null);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(hero);
  const [currentSlide, setCurrentSlide] = useState(0);

  const numElementsPerPage =
    element['layout-carousel-items-per-page'] ??
    (hero ? 1 : DEFAULT_ITEMS_PER_PAGE);

  const locale = useLocale();
  const { elements, error, fetchMore, hasMoreData, isLoading } =
    useCarouselData(element.id, locale.locale, numElementsPerPage);

  const responsiveSettings = useCarouselResponsiveSettings(
    elements,
    numElementsPerPage,
    hero,
  );

  const resumeAutoPlayAfter60Seconds = useDebouncedCallback(() => {
    setShouldAutoPlay(hero);
    if (hero) {
      sliderRef.current?.slickPlay();
    }
  }, 60000);

  useEffect(() => {
    const handleClick = () => {
      // pause the carousel
      setShouldAutoPlay(false);

      // resume rotating again in 60 seconds if its a hero carousel
      resumeAutoPlayAfter60Seconds();
    };

    const node = containerRef.current;
    node?.addEventListener('click', handleClick);

    // Add event listeners to all children
    node?.childNodes.forEach((child) => {
      (child as HTMLElement).addEventListener('click', handleClick);
    });

    // Cleanup function to remove event listeners when component unmounts
    return () => {
      node?.removeEventListener('click', handleClick);
      node?.childNodes.forEach((child) => {
        (child as HTMLElement).removeEventListener('click', handleClick);
      });
    };
  }, [resumeAutoPlayAfter60Seconds]);

  const debouncedFetchMore = useDebouncedCallback(() => {
    if (hasMoreData && !isLoading) {
      void fetchMore();
    }
  }, DEBOUNCE_DELAY);

  const handleSlideChange = useCallback(
    (_current: number, next: number) => {
      setCurrentSlide(next);
      const remainingSlides = elements.length - next;
      const threshold = numElementsPerPage * PRELOAD_THRESHOLD;

      if (remainingSlides <= threshold && hasMoreData && !isLoading) {
        debouncedFetchMore();
      }
    },
    [
      elements.length,
      numElementsPerPage,
      hasMoreData,
      isLoading,
      debouncedFetchMore,
    ],
  );
  const sliderSettings = useMemo(
    () => ({
      dots: hero && elements.length > numElementsPerPage,
      swipeToSlide: true,
      speed: 500,
      autoplay: shouldAutoPlay,
      autoplaySpeed: 5000,
      pauseOnHover: true,
      nextArrow: shouldDisplayNextArrow(
        elements,
        currentSlide,
        responsiveSettings,
        numElementsPerPage,
      ) ? (
        <CustomArrow hero={hero} direction="right" />
      ) : (
        <></>
      ),
      prevArrow:
        currentSlide > 0 ? <CustomArrow hero={hero} direction="left" /> : <></>,
      responsive: responsiveSettings,
      infinite: false,
      beforeChange: handleSlideChange,
      afterChange: (_current: number) => {
        if (
          shouldAutoPlay &&
          element?.elements?.length &&
          _current >= element.elements.length - 1
        ) {
          setTimeout(() => {
            if (shouldAutoPlay) {
              sliderRef.current?.slickGoTo(0);
              setCurrentSlide(0);
            }
          }, 5000);
        }
      },
      lazyLoad: 'ondemand' as const,
      appendDots: (dots: React.ReactNode) => (
        <div className="carousel-dots">
          <ul className="flex justify-center gap-2 mt-4">{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <button
          className="w-3 h-3 rounded-full bg-gray-400 hover:bg-white transition-colors duration-200"
          aria-label="Go to slide"
        />
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hero,
      elements.length,
      numElementsPerPage,
      shouldAutoPlay,
      responsiveSettings,
      handleSlideChange,
      currentSlide,
    ],
  );

  if (error) {
    return (
      <div className="relative mb-6">
        {!hero && (
          <div className="text-text-primary my-2 px-3 text-2xl font-bold sm:px-10 lg:px-14">
            {element.title}
          </div>
        )}
        <div className="flex items-center justify-center min-h-[200px] text-red-400">
          <div className="text-center">
            <p className="mb-2">Failed to load content</p>
            <button
              onClick={() => fetchMore()}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  return elements?.length === 0 ? (
    <></>
  ) : (
    <div className="relative mb-6" ref={containerRef}>
      {!hero && (
        <div className="text-text-primary my-2 px-3 text-2xl font-bold sm:px-10 lg:px-14">
          {element.title}
        </div>
      )}

      <div className={`relative ${hero ? 'h-hero' : ''}`}>
        {isLoading && <LoadingIndicator />}

        <div className={hero ? '' : 'sm:mx-10 lg:mx-14 mx-3 overflow-hidden'}>
          {elements.length > 0 ? (
            <Slider {...sliderSettings} ref={sliderRef}>
              {elements.map((el: CarouselElementType) => (
                <Suspense key={el.id}>
                  <ElementRenderer
                    key={el.id}
                    element={el}
                    hero={hero}
                    numElementsPerPage={numElementsPerPage}
                    autoplay={autoplay}
                  />
                </Suspense>
              ))}
            </Slider>
          ) : (
            isLoading && (
              <div className="flex items-center justify-center min-h-[200px]">
                <div className="text-center">
                  <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-400">Loading content...</p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(InfiniteCarousel);
